import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { BaseService } from '../../services/base.service';
import {
  CdkDragDrop,
  CdkDragMove,
  CdkDragStart,
  CdkDropList,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { Board } from '../../model/board.model';
import { Tasks } from '../../model/tasks.model';
import { parseCurrencyXXX } from '../../helpers';
import { Subject } from 'rxjs';
import { IColumnCard, IFunnel } from '../../interfaces';
import { FUNNEL_ACTION } from '../../enum/constants';

@Component({
  selector: 'app-board',
  templateUrl: './board.component.html',
  styleUrls: ['./board.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardComponent implements OnInit, OnDestroy, OnChanges {
  // Destroys
  private readonly destroy$: Subject<null> = new Subject<null>();

  // Inputs
  @Input() board!: Board;
  @Input() funnel!: IFunnel;
  @Input() isCreate!: Board;
  @Input() createTemplateRef!: TemplateRef<any>;
  @Input() cardTemplateRef!: TemplateRef<any>;
  @Input() connectedList!: string[];

  // Outputs
  @Output() onChangeCard = new EventEmitter();
  @Output() onCreateCard = new EventEmitter();
  @Output() onClickTitleCard = new EventEmitter();
  @Output() onClickCard = new EventEmitter();
  @Output() onClickContactCard = new EventEmitter();
  @Output() onScrollEndColumn = new EventEmitter();
  @Output() onQuickCreate = new EventEmitter();
  @Output() onCardAction = new EventEmitter<{ action: FUNNEL_ACTION; item: IColumnCard }>();

  // Var-s
  public quickDealsId: string = '';
  public offsetHeight: number = 130;
  private nameInputVisibility: Record<string, boolean> = {};

  private scrollSpeed: number = 15;
  private scrollInterval!: NodeJS.Timeout | null;

  protected isOverCard: boolean = false;
  protected isDragging: boolean = false;

  protected bottomMenu = [
    { id: FUNNEL_ACTION.DELETE, name: 'delete', color: '187, 187, 187' },
    { id: FUNNEL_ACTION.FAILED, name: 'failure', color: '255, 87, 51' },
    { id: FUNNEL_ACTION.SUCCESS, name: 'success', color: '73, 255, 51' },
  ];

  @ViewChildren(CdkDropList) dropLists!: QueryList<CdkDropList>;

  protected readonly parseCurrencyXXX = parseCurrencyXXX;

  constructor(
    public readonly authService: AuthService,
    public readonly baseService: BaseService,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    if (!this.connectedList) this.connectedList = this.board?.columns?.map((v: any) => v.id);
    const temp = [];
    temp.push(...(this.board?.columns?.map((v: any) => v.id) || []));
    temp.push(...(this.bottomMenu.map((v) => v.id) || []));
    this.connectedList = temp;
  }

  ngOnChanges(changes: SimpleChanges | any): void {
    if (changes.board && !changes.board.isFirstChange()) {
      const temp = [];
      temp.push(...(this.board?.columns?.map((v: any) => v.id) || []));
      temp.push(...(this.bottomMenu.map((v) => v.id) || []));
      this.connectedList = temp;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public dropGrid(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.board.columns, event.previousIndex, event.currentIndex);
  }

  public drop(event: CdkDragDrop<Tasks[], any>): void {
    const bottomAction = this.bottomMenu.find((a) => a.id === event.container.id);
    if (bottomAction) {
      return;
    }
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
  }

  public toggleNameInput(columnId: string): void {
    if (!this.isNameInputVisible(columnId)) {
      this.nameInputVisibility[columnId] = true;
    }
  }

  public isNameInputVisible(columnId: string): boolean {
    return this.nameInputVisibility[columnId];
  }

  public createCard(column: any): void {
    this.closeCreateCard();
    column.is_create = true;
    this.onQuickCreate.emit();
  }

  public closeCreateCard(): void {
    this.board.columns.forEach((v) => (v.is_create = false));
  }

  public dragDropped(event: any, item: any): void {
    this.isDragging = false;

    const data = { ...event, currentItem: item };
    const bottomAction = this.bottomMenu.find((a) => a.id === event.container.id);

    if (bottomAction) {
      this.onCardAction.emit({ action: bottomAction.id, item: item });
    } else {
      this.onChangeCard.emit({ ...data });
    }
  }

  public startDrag(event: CdkDragStart): void {
    this.isDragging = true;
    // this.connectedList.push(...(this.adsada.map(v => v.id) || []));
    this.offsetHeight = event.source.element.nativeElement.offsetHeight;
    if (window.document.body) window.document.body.classList.add('is-dragging');
  }

  public dragMoved(event: CdkDragMove): void {
    this.checkScroll(event.pointerPosition.x);
  }

  public endedDrag(): void {
    this.isDragging = false;
    this.stopScrolling();
    if (window.document.body) window.document.body.classList.remove('is-dragging');
  }

  public open(item: any): void {
    this.onClickTitleCard.emit(item);
  }

  public clickContact(item: any): void {
    this.onClickContactCard.emit(item);
  }

  private checkScroll(pointerX: number): void {
    const boardElement = document.querySelector('.board');
    if (!boardElement) return;

    const { left, right, width } = boardElement.getBoundingClientRect();

    if (pointerX < left + width * 0.2) {
      this.startScrolling(-this.scrollSpeed);
    } else if (pointerX > right - width * 0.2) {
      this.startScrolling(this.scrollSpeed);
    } else {
      this.stopScrolling();
    }
  }

  private startScrolling(speed: number): void {
    this.stopScrolling();
    this.scrollInterval = setInterval(() => {
      const boardElement = document.querySelector('.board');
      if (boardElement) {
        boardElement.scrollLeft += speed;
      }
    }, 20);
  }

  private stopScrolling(): void {
    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  }

  scrollTo(e: any, column: any): void {
    if (e.target.offsetHeight + e.target.scrollTop >= e.target.scrollHeight - 10) {
      this.onScrollEndColumn.emit(column);
    }
  }

  private refreshPositions() {
    this.dropLists?.forEach((dl: any) => {
      console.log('lists???', dl._dropListRef);
      dl._dropListRef?._cacheParentPositions();
    });
  }
}
